import styled from "@emotion/styled"
import { css, IconButton } from "@mui/material"
import { AllJobs } from "lib/jobs"
import Link from "next/link"
import { useEffect, useState } from "react"
import { trackCareerMoreInfo, trackCareerShare, trackCareerShareMethodOnMobile } from "reusable/gtm"
import {
  ButtonWithArrow,
  CalendarIcon,
  CompanyIcon,
  MapIcon,
  NewText,
  ShareIcon,
  useIsDesktop,
  useIsXLargeDesktop,
} from "ui"
import { sharedColors } from "ui/theme/colors"
import {
  convertISODateString,
  getNonUmbracoURL,
  stripHtmlForPreview,
  truncateString,
} from "utils/utils"

const StyledContainer = styled.div`
  /* box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px; */
  box-shadow: 0px 0px 20px 0px rgba(130, 136, 148, 0.3);
  margin:15px 0;

  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: white;
  box-sizing: border-box;
  border-left: solid 6px ${sharedColors.yellow};
  display: flex;
  flex-direction: column;
  padding: 28px 24px 32px 20px;
  min-height: 514px;
  width: 100%;
  gap: 28px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin:0;
    padding: 18px 24px 21px 18px;
    min-height: 300px;
    /* width: 466px; */
    gap: 18px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    /* margin-bottom: 0; */
    padding: 18px 24px 21px 18px;
    min-height: 300px;
    /* width: 600px; */
    gap: 18px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 28px 36px 32px 28px;
    min-height: 400px;
    /* width: 700px; */
    gap: 28px;
  }
    height: 100%;
`
const Header = styled.div`
  display: flex;
  flex-direction: column;
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 10px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    gap: 16px;
  }
`
const Description = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledShareDiv = styled.div<{ isCopied: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 200px;
  ${(props) =>
    props.isCopied
      ? css`
          background-color:rgba(21, 62, 105, 0.12);
        `
      : ""}
  ${(props) => props.theme.breakpoints.down("sm")} {
    align-self:flex-end

  }
`
const StyledIconButton = styled(IconButton)`
  width: 32px;
  height: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 22px;
    height: 22px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 32px;
    height: 32px;
  }
  padding: 0px;
`
const IconTexts = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`
const IconText = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  margin-top: auto;
`
type Props = {
  data: AllJobs[number]
}

function JobCard({ data }: Props) {
  const isDesktop = useIsDesktop()
  const isLargeDesktop = useIsXLargeDesktop()
  const [isCopied, setIsCopied] = useState(false)

  const truckGoogleEvent = () => {
    if (!data) {
      return
    }
    trackCareerShare(data.jobTitle!, data.companies, data.date!, data.location)
  }

  const shareUrl = () => {
    const urlToCopy = `${window.location.host}${getNonUmbracoURL(data.url)}`
    if (isDesktop) {
      navigator.clipboard.writeText(urlToCopy)
      setIsCopied(true)
    } else {
      if (navigator.share) {
        navigator.share({
          title: "שיתוף משרה",
          url: urlToCopy,
        }).then(() => {
          trackCareerShareMethodOnMobile("share_successful");
        })
          .catch(() => {
            trackCareerShareMethodOnMobile("share_canceled");
          })
      }
    }
    truckGoogleEvent();
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setIsCopied(false)
    }, 5000)

    return () => {
      clearTimeout(handler)
    }
  }, [isCopied])

  const handleExtraInfoButtonClicked = () => {
    if (!data) {
      return
    }
    trackCareerMoreInfo(data.jobTitle!, data.companies, data.date!, data.location)
  }

  return (
    <StyledContainer>
      <Header>
        <NewText
          component={"p"}
          desktopFontSize={12}
          desktopLineHeight={"16px"}
          largeDesktopFontSize={18}
          largeDesktopLineHeight={"24px"}
          mobileFontSize={16}
          mobileLineHeight={"20px"}
          color={sharedColors.darkGray}
          alignSelf={"flex-start"}
        >
          {data.parentTitle}
        </NewText>
        <NewText
          component={"h3"}
          largeDesktopFontSize={28}
          largeDesktopLineHeight="35.84px"
          desktopFontSize={18}
          desktopLineHeight={"23.04px"}
          mobileFontSize={24}
          mobileLineHeight={"33.6px"}
          desktopFontWeight={500}
        >
          {data.jobTitle}
        </NewText>
        <StyledShareDiv isCopied={isCopied}>
          <StyledIconButton onClick={shareUrl} aria-label={`שיתוף משרה ${data.jobTitle}`}>
            <ShareIcon />
          </StyledIconButton>
          <NewText
            component={"span"}
            largeDesktopFontSize={16}
            largeDesktopLineHeight="24px"
            desktopFontSize={12}
            desktopLineHeight={"16px"}
            mobileFontSize={14}
            mobileLineHeight={"20px"}
            color="black"
          >
            {isCopied ? "הקישור הועתק בהצלחה" : "שיתוף משרה"}
          </NewText>
        </StyledShareDiv>
      </Header>
      <Body>
        <IconTexts>
          <IconText>
            <CalendarIcon
              height={isLargeDesktop ? 28 : 18}
              width={isLargeDesktop ? 28 : 18}
              aria-label="תאריך פרסום"
            />

            <NewText
              component={"p"}
              largeDesktopFontSize={16}
              largeDesktopLineHeight="24px"
              desktopFontSize={12}
              desktopLineHeight={"16px"}
              mobileFontSize={14}
              mobileLineHeight={"20px"}
              color={sharedColors.darkGray}
            >
              {convertISODateString(data.date)}
            </NewText>
          </IconText>
          <IconText>
            <CompanyIcon
              height={isLargeDesktop ? 28 : 18}
              width={isLargeDesktop ? 28 : 18}
              aria-label="שם החברה"
            />

            {data.companies.map((el) => (
              <NewText
                key={el}
                component={"p"}
                largeDesktopFontSize={16}
                largeDesktopLineHeight="24px"
                desktopFontSize={12}
                desktopLineHeight={"16px"}
                mobileFontSize={14}
                mobileLineHeight={"20px"}
                color={sharedColors.darkGray}
              >
                {el}
              </NewText>
            ))}
          </IconText>
          <IconText>
            <MapIcon
              height={isLargeDesktop ? 28 : 18}
              width={isLargeDesktop ? 28 : 18}
              aria-label="מיקום המשרה"
            />

            <NewText
              component={"p"}
              largeDesktopFontSize={16}
              largeDesktopLineHeight="24px"
              desktopFontSize={12}
              desktopLineHeight={"16px"}
              mobileFontSize={14}
              mobileLineHeight={"20px"}
              color={sharedColors.darkGray}
            >
              {data?.location.join(",")}
            </NewText>
          </IconText>
        </IconTexts>
        <Description>
          <NewText
            component={"h4"}
            largeDesktopFontSize={16}
            largeDesktopLineHeight="24px"
            desktopFontSize={11}
            desktopLineHeight={"16px"}
            mobileFontSize={16}
            mobileLineHeight={"24px"}
            color={sharedColors.darkGray}
            desktopFontWeight={500}
          >
            תיאור התפקיד
          </NewText>
          <NewText
            component={"p"}
            largeDesktopFontSize={16}
            largeDesktopLineHeight="24px"
            desktopFontSize={12}
            desktopLineHeight={"18px"}
            mobileFontSize={16}
            mobileLineHeight={"24px"}
            color="black"
          >
            {truncateString(stripHtmlForPreview(data.jobDescription || ""), 300)}
          </NewText>
        </Description>
      </Body>
      <StyledLink href={getNonUmbracoURL(data.url)} aria-label={`למידע נוסף-${data.jobTitle}`} onClick={handleExtraInfoButtonClicked}>
        <ButtonWithArrow
          largeDesktopFontSize={18}
          largeDesktopHeight={40}
          largeDesktopWidth={155}
          desktopHeight={40}
          desktopWidth={155}
          desktopFontSize={12}
          smallDesktopFontSize={12}
          smallDesktopHeight={26}
          smallDesktopWidth={104}
          mobileFontSize={16}
          mobileHeight={40}
          mobileWidth={146}
          desktopFontWeight={400}
          title="למידע נוסף"
          asChild
          backgroundColor={sharedColors.darkBlue}
        />
      </StyledLink>
    </StyledContainer>
  )
}

export default JobCard
